import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Box, Typography, Button, Container, Grid, Avatar, Rating } from '@mui/material';
import { styled } from '@mui/material/styles';
import StarIcon from '@mui/icons-material/Star';
import TrackedButton from '../components/TrackedButton';

const StyledFeatureCard = styled(motion.div)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  borderRadius: '24px',
  padding: '2rem',
  height: '100%',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
}));

const StyledIcon = styled(Box)(({ theme }) => ({
  width: '64px',
  height: '64px',
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '1.5rem',
  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
  boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)',
}));

const StyledStatCard = styled(motion.div)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.03)',
  borderRadius: '24px',
  padding: '2rem',
  textAlign: 'center',
  border: '1px solid rgba(255, 255, 255, 0.05)',
}));

const StyledTestimonialCard = styled(motion.div)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  borderRadius: '24px',
  padding: '2rem',
  height: '100%',
  border: '1px solid rgba(255, 255, 255, 0.1)',
}));

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const Publishers = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <Box sx={{ 
      minHeight: 'calc(100vh - 64px)',
      marginTop: 0,
      background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
      color: 'white',
      overflow: 'hidden',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '100%',
        background: 'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)',
        pointerEvents: 'none',
      }
    }}>
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          style={{ paddingTop: '8rem', paddingBottom: '4rem' }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem' },
              fontWeight: 800,
              textAlign: 'center',
              marginBottom: '1.5rem',
              background: 'linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Monetize Your AI Platform
          </Typography>
          <Typography
            variant="h5"
            sx={{
              textAlign: 'center',
              color: '#94A3B8',
              maxWidth: '800px',
              margin: '0 auto',
              marginBottom: '3rem',
            }}
          >
            Join our network of publishers and start earning revenue from AI-powered conversations
          </Typography>
        </motion.div>
      </Container>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <StyledStatCard>
                <Typography variant="h2" sx={{ 
                  fontWeight: 800,
                  background: 'linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}>
                  85%
                </Typography>
                <Typography variant="h6" sx={{ color: '#94A3B8' }}>
                  Revenue Share
                </Typography>
              </StyledStatCard>
            </Grid>
            <Grid item xs={12} md={3}>
              <StyledStatCard>
                <Typography variant="h2" sx={{ 
                  fontWeight: 800,
                  background: 'linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}>
                  10K+
                </Typography>
                <Typography variant="h6" sx={{ color: '#94A3B8' }}>
                  Active Publishers
                </Typography>
              </StyledStatCard>
            </Grid>
            <Grid item xs={12} md={3}>
              <StyledStatCard>
                <Typography variant="h2" sx={{ 
                  fontWeight: 800,
                  background: 'linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}>
                  $5M+
                </Typography>
                <Typography variant="h6" sx={{ color: '#94A3B8' }}>
                  Monthly Payouts
                </Typography>
              </StyledStatCard>
            </Grid>
            <Grid item xs={12} md={3}>
              <StyledStatCard>
                <Typography variant="h2" sx={{ 
                  fontWeight: 800,
                  background: 'linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}>
                  24/7
                </Typography>
                <Typography variant="h6" sx={{ color: '#94A3B8' }}>
                  Support
                </Typography>
              </StyledStatCard>
            </Grid>
          </Grid>
        </motion.div>
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <StyledFeatureCard variants={fadeInUp}>
                <StyledIcon>
                  <Typography variant="h4">💰</Typography>
                </StyledIcon>
                <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
                  Maximize Revenue
                </Typography>
                <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                  Earn up to 85% revenue share with our competitive payout structure
                </Typography>
              </StyledFeatureCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledFeatureCard variants={fadeInUp}>
                <StyledIcon>
                  <Typography variant="h4">⚡</Typography>
                </StyledIcon>
                <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
                  Easy Integration
                </Typography>
                <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                  Simple API integration with comprehensive documentation and support
                </Typography>
              </StyledFeatureCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledFeatureCard variants={fadeInUp}>
                <StyledIcon>
                  <Typography variant="h4">📊</Typography>
                </StyledIcon>
                <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
                  Real-time Analytics
                </Typography>
                <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                  Track performance and earnings with detailed analytics dashboard
                </Typography>
              </StyledFeatureCard>
            </Grid>
          </Grid>
        </motion.div>
      </Container>

      {/* Testimonials Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              textAlign: 'center',
              marginBottom: '3rem',
              background: 'linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Success Stories
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <StyledTestimonialCard>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ width: 56, height: 56, mr: 2 }}>TS</Avatar>
                  <Box>
                    <Typography variant="h6">Tom Smith</Typography>
                    <Typography variant="body2" sx={{ color: '#94A3B8' }}>AI Platform Founder</Typography>
                  </Box>
                </Box>
                <Rating value={5} readOnly sx={{ mb: 2 }} />
                <Typography variant="body1" paragraph>
                  "Integrating Reasonic was seamless, and the revenue has exceeded our expectations. The support team is always there when we need them."
                </Typography>
              </StyledTestimonialCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledTestimonialCard>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ width: 56, height: 56, mr: 2 }}>RJ</Avatar>
                  <Box>
                    <Typography variant="h6">Rachel Johnson</Typography>
                    <Typography variant="body2" sx={{ color: '#94A3B8' }}>Tech Startup CEO</Typography>
                  </Box>
                </Box>
                <Rating value={5} readOnly sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                  "The analytics dashboard gives us complete visibility into our earnings. We've seen a 300% increase in revenue since joining."
                </Typography>
              </StyledTestimonialCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledTestimonialCard>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ width: 56, height: 56, mr: 2 }}>MK</Avatar>
                  <Box>
                    <Typography variant="h6">Mike Kim</Typography>
                    <Typography variant="body2" sx={{ color: '#94A3B8' }}>AI Developer</Typography>
                  </Box>
                </Box>
                <Rating value={5} readOnly sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                  "The API integration was straightforward, and the documentation is excellent. We were up and running in less than a day."
                </Typography>
              </StyledTestimonialCard>
            </Grid>
          </Grid>
        </motion.div>
      </Container>

      {/* CTA Section */}
      <Box sx={{ 
        background: 'linear-gradient(180deg, rgba(15, 23, 42, 0) 0%, rgba(15, 23, 42, 1) 100%)',
        padding: '6rem 0',
        position: 'relative',
        zIndex: 1,
      }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: 'center' }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                marginBottom: '1.5rem',
                background: 'linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Ready to Start Earning?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#94A3B8',
                marginBottom: '3rem',
              }}
            >
              Join our network of successful publishers today
            </Typography>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <TrackedButton
                variant="contained"
                size="large"
                sx={{
                  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                  padding: '1rem 2.5rem',
                  borderRadius: '12px',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                  },
                }}
                trackingName="become_publisher_cta"
                trackingCategory="publisher_acquisition"
                trackingData={{ location: "publishers_page_bottom" }}
              >
                Become a Publisher
              </TrackedButton>
            </motion.div>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default Publishers; 